import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import BRAND from "../brand";
import { ArrowLeft, Loader2, AlertTriangle, Play, Globe } from "lucide-react";
import { ScoreHero } from "./evaluation/ScoreHero";
import { FeedbackTab } from "./evaluation/FeedbackTab";
import { CompetenciesTab } from "./evaluation/CompetenciesTab";
import { TranscriptTab } from "./evaluation/TranscriptTab";

function getLanguageLabel(lang) {
  if (lang === 'hinglish') return 'Hinglish';
  if (lang === 'hi') return 'Hindi';
  return 'English';
}

export default function EvaluationPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const runEvaluation = async () => {
    setEvaluating(true);
    try {
      const { data } = await axios.post(`${API}/conversation/evaluate`, { session_id: sessionId });
      setEvaluation(data.evaluation);
    } catch (e) {
      console.error("[Evaluation] Evaluation failed:", e);
    } finally {
      setEvaluating(false);
    }
  };

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        const sessionRes = await axios.get(`${API}/conversation/${sessionId}`);
        setSession(sessionRes.data);
        try {
          const evalRes = await axios.get(`${API}/conversation/${sessionId}/evaluation`);
          setEvaluation(evalRes.data.evaluation);
        } catch {
          await runEvaluation();
        }
      } catch (e) {
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchSessionData();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || evaluating) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 fade-up">
          <div className="w-20 h-20 rounded-full bg-[#0B1F3B]/5 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-[#2563EB] animate-spin" />
          </div>
          <div className="text-center">
            <p className="text-[#0B1F3B] text-lg font-semibold">
              {evaluating ? "Analyzing your conversation..." : "Loading evaluation..."}
            </p>
            {evaluating && <p className="text-slate-400 text-sm mt-1">Our AI coach is reviewing every detail</p>}
          </div>
        </div>
      </div>
    );
  }

  if (!session || !evaluation) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0B1F3B] mb-2">Evaluation not found</h2>
          <Link to="/dashboard"><Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90">Return to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-slate-500 hover:text-[#0B1F3B]"><ArrowLeft className="w-5 h-5" /></Button>
              </Link>
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-7 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h1 className="text-lg font-bold text-[#0B1F3B]">{BRAND.terms.evaluation}</h1>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-400">{session.scenario?.name}</p>
                  <Badge variant="outline" className="text-xs border-[#2563EB]/20 text-[#2563EB]">
                    <Globe className="w-3 h-3 mr-1" />
                    {getLanguageLabel(session.selected_language)}
                  </Badge>
                </div>
              </div>
            </div>
            <Link to="/simulator">
              <Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 btn-premium" data-testid="practice-again-btn">
                <Play className="w-4 h-4 mr-2" /> Practice Again
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ScoreHero evaluation={evaluation} session={session} />

        <Tabs defaultValue="feedback" className="space-y-6">
          <TabsList className="bg-white border border-slate-200/60 rounded-xl p-1">
            <TabsTrigger value="feedback" data-testid="feedback-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">Coaching Feedback</TabsTrigger>
            <TabsTrigger value="competencies" data-testid="competencies-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">Competencies</TabsTrigger>
            <TabsTrigger value="transcript" data-testid="transcript-tab" className="rounded-lg data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">Transcript</TabsTrigger>
          </TabsList>

          <TabsContent value="feedback"><FeedbackTab evaluation={evaluation} /></TabsContent>
          <TabsContent value="competencies"><CompetenciesTab evaluation={evaluation} /></TabsContent>
          <TabsContent value="transcript"><TranscriptTab session={session} /></TabsContent>
        </Tabs>
      </main>

      <footer className="border-t border-slate-200/60 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-6 w-auto opacity-60" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-sm text-slate-400">{BRAND.productName}</span>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
